// @ts-nocheck
import { formatMYR } from "@kawkaw/shared";
import { useEffect, useState, useMemo } from "react";
import { Search, X } from "lucide-react";
import { Select, Dropdown, Table, Button } from "../components/admin-ui";
import { EllipsisOutlined, ShoppingOutlined, CarOutlined, CreditCardOutlined, BankOutlined, DownloadOutlined, ContainerOutlined, DollarOutlined, ReloadOutlined, WalletOutlined } from "../components/admin-icons";
import { StatusBadge } from "../components/StatusBadge";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { MenuProps } from "../components/admin-ui";
import type { Transaction } from "@kawkaw/shared";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { AdminShell } from "../layouts/AdminShell";
import { useAdminTransactions, useAdminTransaction, useAdminTransactionsSummary, fetchAdminTransactionsForExport } from "../api/hooks";
import { downloadReceipt, type ReceiptTxn } from "./ReceiptModal";
import { formatMytStamp, formatOrderDate, formatOrderTime } from "../utils/formatLocaleDate";
import { formatTransactionId } from "../utils/transactionUi";
import { useElementHeight } from "../hooks/useElementHeight";
import { issueRefundPath, refundActiveNav } from "../utils/refundNavigation";

// Backend transaction includes order join: { id, order_code, type, total }
type TxnWithOrder = Transaction & {
  reference: string | null;
  method: string;
  order?: {
    id: string;
    order_code: string;
    type: string;
    total: string;
    customer_name?: string | null;
    customer_phone?: string | null;
    customer?: { name?: string | null; phone?: string | null } | null;
  };
};

const TRANSACTIONS_PAGE_CLASS = [
  "admin-list-page",
  "bg-[#f8f9fa] px-8 pb-6 pt-7",
  "[&_table]:border-separate [&_table]:border-spacing-0 [&_table]:bg-transparent",
  "[&_th]:h-12 [&_th]:border-b-0 [&_th]:bg-[rgba(243,244,245,0.5)] [&_th]:px-6 [&_th]:py-4 [&_th]:text-xs [&_th]:font-extrabold [&_th]:uppercase [&_th]:tracking-[0.6px] [&_th]:text-[#534434]",
  "[&_td]:min-h-[88px] [&_td]:border-b [&_td]:border-[#f8fafc] [&_td]:bg-transparent [&_td]:px-6 [&_td]:py-8 [&_td]:align-middle [&_td]:text-[#191c1d]",
  "[&_tr:hover_td]:bg-transparent [&_tbody_tr]:cursor-default",
  "[&_th:first-child]:pl-6 [&_td:first-child]:pl-6 [&_th:last-child]:pr-12 [&_td:last-child]:pr-12 [&_th:last-child]:text-left [&_td:last-child]:text-left",
].join(" ");

const TRANSACTIONS_FILTER_CLASS = "fixed right-0 top-16 z-40 bg-[#f8f9fa] px-8 pb-4 pt-8 left-64 max-[900px]:left-[76px] max-[900px]:px-4 min-[901px]:max-[1280px]:left-[220px] min-[901px]:max-[1280px]:px-5";

function TxnStatusPill({ status }: { status: string }) {
  return <StatusBadge status={status} kind="transaction" className="admin-txn-status admin-data-badge" />;
}

function transactionDisplayId(row: TxnWithOrder) {
  return { label: formatTransactionId(row.id), title: row.id ?? "-" };
}

function getTransactionCustomer(row: TxnWithOrder) {
  const name = row.order?.customer_name?.trim() || row.order?.customer?.name?.trim();
  const phone = row.order?.customer_phone?.trim() || row.order?.customer?.phone?.trim();
  return { name, phone };
}

const tableRowClass = (_: TxnWithOrder, index: number) => (
  index % 2 === 0
    ? "[&>td]:!bg-white hover:[&>td]:!bg-white"
    : "[&>td]:!bg-transparent hover:[&>td]:!bg-transparent"
);

export function TransactionsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string | undefined>();
  const [method, setMethod] = useState<string | undefined>();
  const [orderType, setOrderType] = useState<string | undefined>();
  const [source, setSource] = useState<string | undefined>();
  const [datePreset, setDatePreset] = useState("7d");
  const [exporting, setExporting] = useState(false);
  const [receiptTxnId, setReceiptTxnId] = useState<string | undefined>();
  const { data: receiptTxnData } = useAdminTransaction(receiptTxnId);
  const [filterRef, filterHeight] = useElementHeight<HTMLDivElement>();

  useEffect(() => {
    if (!receiptTxnId || !receiptTxnData?.order?.kopitiam || !receiptTxnData?.order?.items) return;
    downloadReceipt(receiptTxnData as ReceiptTxn);
    setReceiptTxnId(undefined);
  }, [receiptTxnData, receiptTxnId]);

  const dateRange = useMemo(() => {
    const days = datePreset === "7d" ? 7 : datePreset === "30d" ? 30 : datePreset === "90d" ? 90 : null;
    if (!days) return {};
    return {
      from: dayjs().subtract(days, "day").startOf("day").toISOString(),
      to: dayjs().endOf("day").toISOString(),
    };
  }, [datePreset]);

  const datePresetOptions = useMemo(
    () => [
      { value: "7d", label: t("transactions.datePreset7d") },
      { value: "30d", label: t("transactions.datePreset30d") },
      { value: "90d", label: t("transactions.datePreset90d") },
      { value: "all", label: t("transactions.datePresetAll") },
    ],
    [t],
  );

  const paymentFilterOptions = useMemo(
    () => [
      { value: "cash", label: t("refunds.payCash") },
      { value: "fpx", label: t("refunds.payFpx") },
      { value: "visa", label: t("refunds.payVisa") },
      { value: "grabpay", label: t("refunds.payGrab") },
      { value: "tng", label: t("refunds.payTng") },
      { value: "ewallet", label: t("refunds.payEwallet") },
    ],
    [t],
  );

  const statusFilterOptions = useMemo(
    () => [
      { value: "success", label: t("transactions.statusSuccess") },
      { value: "pending", label: t("transactions.statusPending") },
      { value: "partial_refund", label: t("transactions.statusPartialRefund") },
      { value: "refunded", label: t("transactions.statusRefunded") },
      { value: "failed", label: t("transactions.statusFailed") },
    ],
    [t],
  );

  const orderTypeFilterOptions = useMemo(
    () => [
      { value: "pickup", label: t("orders.type.pickup") },
      { value: "delivery", label: t("orders.type.delivery"), disabled: source === "walkin" },
    ],
    [t, source],
  );

  useEffect(() => {
    if (source === "walkin" && orderType === "delivery") setOrderType(undefined);
  }, [source, orderType]);

  const sourceFilterOptions = useMemo(
    () => [
      { value: "walkin", label: t("transactions.sourceWalkin") },
      { value: "customer", label: t("transactions.sourceCustomerApp") },
    ],
    [t],
  );

  const paymentMethodLabel = useMemo(() => {
    const map: Record<string, string> = {
      cash: t("refunds.payCash"),
      fpx: t("refunds.payFpx"),
      visa: t("refunds.payVisa"),
      grabpay: t("refunds.payGrab"),
      tng: t("refunds.payTng"),
      boost: t("refunds.payBoost"),
      ewallet: t("refunds.payEwallet"),
    };
    return (m: string) => map[m] ?? m.toUpperCase();
  }, [t]);

  const { data, isLoading } = useAdminTransactions({
    page, limit: 10,
    q: search || undefined,
    status,
    method,
    type: orderType,
    source,
    ...dateRange,
  });

  const txns = (data?.data ?? []) as TxnWithOrder[];
  const totalCount = data?.meta.total ?? 0;

  const { data: summaryData } = useAdminTransactionsSummary({
    q: search || undefined,
    status,
    method,
    type: orderType,
    source,
    ...dateRange,
  });
  const summary = summaryData?.data;

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await fetchAdminTransactionsForExport({
        q: search || undefined, status, method, type: orderType, source, ...dateRange,
      }) as TxnWithOrder[];
      const csvRows = rows.map((row) => {
        const customer = getTransactionCustomer(row);
        return {
          TransactionId: row.id,
          OrderCode: row.order?.order_code ?? "",
          Source: row.order?.order_code?.startsWith("WI-") ? t("reports.categoryWalkin") : t("reports.categoryCustomerApp"),
          Customer: customer.name ?? "",
          OrderType: row.order?.type ?? "",
          AmountRM: row.amount,
          Method: row.method,
          Status: row.status,
          PaidAt: row.paid_at ? formatMytStamp(row.paid_at) : "",
        };
      });
      if (!csvRows.length) return;
      const headers = Object.keys(csvRows[0]);
      const csv = [
        headers.join(","),
        ...csvRows.map((r) => headers.map((h) => `"${String((r as Record<string, unknown>)[h] ?? "").replace(/"/g, '""')}"`).join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions-${formatMytStamp(new Date()).slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const TH = (label: string) => (
    <span className="text-[11px] font-black uppercase tracking-[0.5px] text-[#7a6f65]">{label}</span>
  );

  const columns: ColumnsType<TxnWithOrder> = useMemo(() => [
    {
      title: TH(t("transactions.colTxnId")),
      dataIndex: "reference", key: "ref", width: 160,
      render: (_, row) => {
        const displayId = transactionDisplayId(row);
        return (
          <span className="admin-txn-id admin-data-code font-mono text-xs font-black text-[#191c1d]" title={displayId.title}>
            {displayId.label}
          </span>
        );
      },
    },
    {
      title: TH(t("transactions.colCustomerOrder")),
      key: "customer_order", width: 180,
      render: (_, row) => {
        const customer = getTransactionCustomer(row);
        return (
          <div className="min-w-0">
            <div className="admin-txn-customer admin-data-customer truncate text-sm font-black text-[#191c1d]">{customer.name || t("transactions.customerLabel")}</div>
            <div className="admin-txn-order-code admin-txn-secondary admin-data-secondary mt-1 text-xs font-bold text-[#7a6f65]">#{row.order?.order_code ?? "-"}</div>
          </div>
        );
      },
    },
    {
      title: TH(t("transactions.colOrderType")),
      key: "order_type", width: 120,
      render: (_, row) => {
        const isDelivery = row.order?.type === "delivery";
        return (
          <StatusBadge
            status={isDelivery ? "delivery" : "pickup"}
            kind="orderType"
            size="xs"
            className="admin-txn-type admin-data-badge"
            icon={isDelivery ? <CarOutlined className="text-[11px]" /> : <ShoppingOutlined className="text-[11px]" />}
          />
        );
      },
    },
    {
      title: TH(t("transactions.colAmount")),
      dataIndex: "amount", key: "amount", width: 120,
      render: (v, row) => (
        <div>
          <div className="admin-txn-amount admin-data-amount text-[15px] font-black text-[#191c1d]">{formatMYR(Number(v as string))}</div>
          {row.status === "partial_refund" && (
            <div className="admin-txn-refund-note mt-1 text-xs font-bold text-[#a14000]">{t("transactions.partialRefNote")}</div>
          )}
        </div>
      ),
    },
    {
      title: TH(t("transactions.colPayment")),
      dataIndex: "method", key: "method", width: 160,
      render: (v) => (
        <div className="admin-txn-payment inline-flex items-center gap-2 text-sm font-bold text-[#3f4548]">
          {(v as string) === "fpx" ? <BankOutlined /> : <CreditCardOutlined />}
          {paymentMethodLabel(v as string)}
        </div>
      ),
    },
    {
      title: TH(t("transactions.colStatus")),
      dataIndex: "status", key: "status", width: 160,
      render: (v) => <TxnStatusPill status={v as string} />,
    },
    {
      title: TH(t("transactions.colDateTime")),
      dataIndex: "paid_at", key: "date", width: 140,
      render: (v) => v ? (
        <div>
          <div className="admin-txn-time admin-data-time text-sm font-semibold leading-5 text-[#191c1d]">{formatOrderTime(v as string, i18n.language)}</div>
          <div className="admin-txn-date admin-txn-secondary admin-data-secondary text-xs leading-4 text-[#534434]">{formatOrderDate(v as string, i18n.language)}</div>
        </div>
      ) : "-",
    },
    {
      title: TH(t("orders.colAction")), key: "actions", width: 100, fixed: "right",
      render: (_, row) => {
        const items: MenuProps["items"] = [
          {
            key: "view",
            label: <span className="text-xs font-black">{t("transactions.actionViewDetails")}</span>,
            onClick: () => { navigate(`/transactions/${row.id}`); },
          },
          {
            key: "refund",
            label: <span className="text-xs font-black">{t("transactions.actionRefunds")}</span>,
            disabled: row.status !== "success",
            onClick: () => { navigate(issueRefundPath(row.id, "transactions"), { state: { activeNav: refundActiveNav("transactions") } }); },
          },
          {
            key: "receipt",
            label: <span className="text-xs font-black">{t("transactions.actionReceipt")}</span>,
            onClick: () => { setReceiptTxnId(row.id); },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <button type="button" className="admin-txn-more admin-data-more inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center border-none bg-transparent p-2.5 text-slate-500">
              <EllipsisOutlined className="text-lg" />
            </button>
          </Dropdown>
        );
      },
    },
  ], [t, navigate, paymentMethodLabel]);

  return (
    <>
    <AdminShell>
      <div className={`admin-txn-page ${TRANSACTIONS_PAGE_CLASS}`}>
        <div className="mx-auto">
          <div ref={filterRef} className={`admin-txn-filter admin-fixed-filter ${TRANSACTIONS_FILTER_CLASS}`}>
            <div className="mx-auto">
              <div className="admin-txn-toolbar mb-5 flex flex-wrap items-end justify-between gap-4">
                <div className="admin-txn-heading flex min-w-[220px] flex-col items-start gap-[9px]">
                  <div className="admin-page-title m-0 text-2xl font-black leading-8 tracking-[-0.6px] text-[#191c1d]">{t("transactions.pageTitle")}</div>
                  <div className="admin-txn-period flex w-full items-center gap-2">
                    <span className="inline-block size-2 shrink-0 rounded-full bg-[#006c49]" />
                    <span className="text-xs font-medium leading-4 text-[#534434]">
                      {datePresetOptions.find((item) => item.value === datePreset)?.label}
                    </span>
                  </div>
                </div>

                <div className="admin-txn-tools">
                  <div className="admin-txn-search relative w-full max-w-[420px] min-w-[260px] sm:w-[360px] xl:w-[420px]">
                    <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#867461]" />
                    <input
                      type="text"
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder={t("transactions.searchPlaceholderWide")}
                      className="admin-txn-search-input h-11 w-full rounded-lg border border-[#e7e1db] bg-white pl-11 pr-11 text-sm font-semibold text-[#191c1d] outline-none shadow-[0_12px_28px_rgba(25,28,29,0.06)] placeholder:text-[#867461] focus:border-[#ea580c] focus:ring-4 focus:ring-[#ea580c]/10"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        className="admin-txn-search-clear absolute right-3 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full border-none bg-[#f3f4f5] text-[#534434] transition hover:bg-[#ffedd5] hover:text-[#ea580c] sm:size-6"
                        aria-label={t("common.clearFilters")}
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={exporting}
                    onClick={() => void handleExport()}
                    className="admin-txn-export h-11 shrink-0 rounded-lg font-bold"
                  >
                    {t("common.exportCsv")}
                  </Button>
                </div>
              </div>

              <div className="admin-txn-stats mb-5 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                {[
                  { label: t("transactions.statTotalTxns"), value: totalCount.toLocaleString(), icon: <ContainerOutlined />, className: "" },
                  { label: t("transactions.statTotalRevenue"), hint: t("transactions.statTotalRevenueHint"), value: summary ? formatMYR(summary.totalRevenue) : "-", icon: <DollarOutlined />, className: "" },
                  { label: t("transactions.statTotalRefunded"), value: summary ? formatMYR(summary.totalRefunded) : "-", icon: <ReloadOutlined />, className: "admin-txn-stat-card-refund" },
                  { label: t("transactions.statNetRevenue"), value: summary ? formatMYR(summary.netRevenue) : "-", icon: <WalletOutlined />, className: "admin-kpi-card-revenue" },
                ].map((card) => (
                  <div key={card.label} title={(card as { hint?: string }).hint} className={`admin-kpi-card admin-txn-stat-card flex flex-col ${card.className}`}>
                    <div className="admin-kpi-heading flex">
                      <div className="admin-kpi-label admin-txn-stat-label">{card.label}</div>
                      <div className="admin-kpi-icon" aria-hidden="true">{card.icon}</div>
                    </div>
                    <div className="admin-kpi-value admin-txn-stat-value flex-1 tabular-nums">
                      {card.value.startsWith("RM ") ? (
                        <div className="admin-kpi-money">
                          <span className="admin-kpi-currency">RM</span>{" "}
                          <span className="admin-kpi-amount">{card.value.slice(3)}</span>
                        </div>
                      ) : card.value}
                    </div>
                    {(card as { hint?: string }).hint && <div className="mt-1 text-[11px] leading-snug text-[#8a817a]">{(card as { hint?: string }).hint}</div>}
                  </div>
                ))}
              </div>

              <div className="admin-txn-filter-panel flex items-center gap-3 rounded-lg border border-[#eee8e2] bg-white/85 p-2 shadow-[0_12px_30px_rgba(25,28,29,0.05)] backdrop-blur max-lg:flex-col max-lg:items-stretch">
                <div className="admin-txn-filter-fields grid min-w-0 flex-1 grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
                  <Select
                    value={datePreset}
                    onChange={(v) => { setDatePreset(v as string); setPage(1); }}
                    className="w-full"
                    style={{ height: 40 }}
                    options={datePresetOptions}
                  />
                  <Select
                    placeholder={t("transactions.filterAllPaymentMethods")}
                    allowClear
                    value={method}
                    onChange={(v) => { setMethod(v as string | undefined); setPage(1); }}
                    className="w-full"
                    style={{ height: 40 }}
                    options={paymentFilterOptions}
                  />
                  <Select
                    placeholder={t("transactions.filterAllStatus")}
                    allowClear
                    value={status}
                    onChange={(v) => { setStatus(v as string | undefined); setPage(1); }}
                    className="w-full"
                    style={{ height: 40 }}
                    options={statusFilterOptions}
                  />
                  <Select
                    placeholder={t("transactions.filterAllOrderTypes")}
                    allowClear
                    value={orderType}
                    onChange={(v) => { setOrderType(v as string | undefined); setPage(1); }}
                    className="w-full"
                    style={{ height: 40 }}
                    options={orderTypeFilterOptions}
                  />
                  <Select
                    placeholder={t("transactions.filterAllSources")}
                    allowClear
                    value={source}
                    onChange={(v) => { setSource(v as string | undefined); setPage(1); }}
                    className="w-full"
                    style={{ height: 40 }}
                    options={sourceFilterOptions}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Reserve the measured fixed-header space. The table below stays in
              normal document flow and scrolls with the page, not in a height-limited panel. */}
          <div style={{ height: filterHeight ? filterHeight + 24 : 300 }} />
          <div className="admin-txn-table-panel admin-data-table-panel border-none bg-transparent shadow-none">
            <Table<TxnWithOrder>
              columns={columns}
              dataSource={txns}
              rowKey="id"
              loading={isLoading}
              rowClassName={tableRowClass}
              size="middle"
              scroll={{ x: 1000 }}
              pagination={{ current: page, total: totalCount, pageSize: 10, showAlways: true, onChange: (p) => setPage(p) }}
            />
          </div>
        </div>
      </div>

    </AdminShell>

  </>
  );
}
